import { computed, signal } from '@angular/core';
import type { Document, DocumentUsage } from '../model';
import type { DocumentLinkDialogData } from './document-link.model';
import { CanvasStateService } from './canvas-state.service';
import { peekDocumentLinkFormState as peekDraftFormState } from './document-link-form-state.util';

export interface DocumentLinkSelectionContext {
  documents: () => Document[];
}

/** Selection, canvas groups, document usage presentation and pending draft attachments. */
export class DocumentLinkSelectionState {
  private readonly pendingDocumentIds = signal<Set<string>>(new Set());
  readonly selectedDocuments = signal<Map<string, Document>>(new Map());
  readonly selectedCount = computed(() => this.selectedDocuments().size);
  readonly groupsEnabled = signal(false);
  readonly groupedDocuments = computed(() => {
    const documents = new Map(this.context.documents().map((document) => [document.id, document]));
    return this.canvasState.groupsOnCanvas().map((group) => ({
      group,
      documents: (this.canvasState.groupedDocumentIds().get(group.id) ?? [])
        .map((documentId) => documents.get(documentId))
        .filter((document): document is Document => Boolean(document)),
    }));
  });
  readonly ungroupedDocuments = computed(() => {
    const groupedIds = new Set(
      Array.from(this.canvasState.groupedDocumentIds().values()).flatMap((groupIds) => groupIds)
    );
    return this.context.documents().filter((document) => !groupedIds.has(document.id));
  });
  readonly expandedUsageDocuments = signal<Set<string>>(new Set());
  constructor(
    private readonly data: DocumentLinkDialogData,
    private readonly canvasState: CanvasStateService,
    private readonly context: DocumentLinkSelectionContext
  ) {}
  selectUploadedDocument(documentId: string) {
    this.groupsEnabled.set(false);
    const doc = this.context.documents().find((item) => item.id === documentId);
    if (!doc) {
      return;
    }
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      next.set(doc.id, doc);
      return next;
    });
  }

  documentUsages(document: Document) {
    return document.usages ?? [];
  }

  usageCount(document: Document) {
    return this.documentUsages(document).length;
  }

  usageCountLabel(document: Document) {
    const count = this.usageCount(document);
    return count === 1 ? 'usage_tr' : 'usages_tr';
  }

  isUsageExpanded(document: Document) {
    return this.expandedUsageDocuments().has(document.id);
  }

  toggleUsageList(document: Document, event: Event) {
    event.stopPropagation();
    this.expandedUsageDocuments.update((current) => {
      const next = new Set(current);
      if (next.has(document.id)) {
        next.delete(document.id);
      } else {
        next.add(document.id);
      }
      return next;
    });
  }

  selectDocument(document: Document) {
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      if (next.has(document.id)) {
        next.delete(document.id);
      } else {
        next.set(document.id, document);
      }
      return next;
    });
  }

  isGroupFullySelected(documents: Document[]) {
    return documents.length > 0 && documents.every((document) => this.isSelected(document));
  }

  isGroupPartiallySelected(documents: Document[]) {
    const selected = documents.filter((document) => this.isSelected(document)).length;
    return selected > 0 && selected < documents.length;
  }

  toggleGroup(documents: Document[], checked: boolean) {
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      for (const document of documents) {
        if (checked) {
          next.set(document.id, document);
        } else {
          next.delete(document.id);
        }
      }
      return next;
    });
  }

  isSelected(document: Document) {
    return this.selectedDocuments().has(document.id);
  }

  hasUsages(document: Document) {
    return this.documentUsages(document).length > 0;
  }

  usageIcon(usage: DocumentUsage): string {
    switch (usage.type) {
      case 'chain':
      case 'chain_pending':
        return 'link';
      case 'product_attribute':
      case 'product_element':
        return 'inventory_2';
      case 'report':
      case 'report_detail_product':
      case 'report_detail_article':
      case 'report_detail_article_note':
      case 'report_product_notes':
      case 'attribute_report_notes':
        return 'assessment';
      case 'company':
        return 'apartment';
      case 'site':
      case 'site_manual':
        return 'forest';
      default:
        return 'link';
    }
  }

  usageLabel(usage: DocumentUsage): string {
    const label = usage.label ?? '';
    switch (usage.type) {
      case 'chain':
      case 'chain_pending':
        return label ? `Chain: ${label}` : 'Chain';
      case 'product_attribute':
        return label ? `Attribute: ${label}` : 'Attribute';
      case 'product_element':
        return label ? `Packaging element: ${label}` : 'Packaging element';
      case 'report':
      case 'report_detail_product':
      case 'report_detail_article':
      case 'report_detail_article_note':
      case 'report_product_notes':
      case 'attribute_report_notes':
        return label ? `Report: ${label}` : 'Report';
      case 'company':
        return label ? `Company: ${label}` : 'Company';
      case 'site':
      case 'site_manual':
        return label ? `Site: ${label}` : 'Site';
      default:
        return label || usage.type;
    }
  }

  isPendingDocument(document: Document) {
    return this.pendingDocumentIds().has(document.id);
  }

  refreshPendingDocumentIds() {
    const formStateKey = this.data.formStateKey ?? null;
    if (!formStateKey) {
      this.pendingDocumentIds.set(new Set());
      return;
    }
    const savedState = peekDraftFormState<unknown>(formStateKey);
    this.pendingDocumentIds.set(new Set(this.extractDocumentIds(savedState)));
  }

  extractDocumentIds(value: unknown): string[] {
    const ids = new Set<string>();
    const visit = (node: unknown) => {
      if (!node) {
        return;
      }
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== 'object') {
        return;
      }
      const record = node as Record<string, unknown>;
      const id = record['id'];
      if (typeof id === 'string' && id.trim()) {
        ids.add(id);
      }
      for (const key of [
        'documents',
        'noteDocuments',
        'manualDocuments',
        'documentIds',
        'manualDocumentIds',
        'attributeDraft',
      ]) {
        if (key in record) {
          visit(record[key]);
        }
      }
    };
    visit(value);
    return Array.from(ids);
  }
}
